import { useState, useMemo, useRef } from 'react'
import { useFinance } from '../../context/FinanceContext'
import { getThemeColors } from '../../lib/constants'
import { formatCurrency, formatPercent } from '../../lib/formatters'
import { calculateNetWorth } from '../../lib/calculations/netWorth'
import GlassCard from '../ui/GlassCard'
import Button from '../ui/Button'
import { useSubscription } from '../../context/SubscriptionContext'
import { trackExportReport } from '../../lib/analytics'
import { addChartToPDF } from '../../lib/pdfChartCapture'
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts'

function InputField({ label, value, onChange, prefix }) {
  return (
    <div>
      <label className="block text-xs font-medium text-muted mb-1.5">{label}</label>
      <div className="relative">
        {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-faint text-sm">{prefix}</span>}
        <input
          type="number"
          value={value}
          onChange={e => onChange(parseFloat(e.target.value) || 0)}
          className={`w-full bg-input border border-input rounded-xl py-2.5 text-sm text-heading focus:outline-none focus:border-primary transition ${prefix ? 'pl-7' : 'pl-3'} pr-3`}
        />
      </div>
    </div>
  )
}

function ResultCard({ label, value, color, highlight }) {
  return (
    <GlassCard className={`!p-4 ${highlight ? 'ring-1' : ''}`} style={highlight ? { borderColor: color + '44' } : {}}>
      <p className="text-xs text-muted mb-1">{label}</p>
      <p className={`font-bold ${highlight ? 'text-xl' : 'text-lg'}`} style={{ color }}>{value}</p>
    </GlassCard>
  )
}

export default function NetWorthTracker() {
  const { state } = useFinance()
  const { isPro } = useSubscription()
  const colors = getThemeColors(state.theme)
  const assetChartRef = useRef(null)
  const liabilityChartRef = useRef(null)

  const [assets, setAssets] = useState({
    cash: 15000,
    investments: 50000,
    property: 250000,
    vehicles: 20000,
    other: 5000,
  })

  const [liabilities, setLiabilities] = useState({
    mortgage: 200000,
    studentLoans: 25000,
    autoLoans: 15000,
    creditCards: 3000,
    other: 0,
  })

  const updateAsset = (key, val) => setAssets(prev => ({ ...prev, [key]: val }))
  const updateLiability = (key, val) => setLiabilities(prev => ({ ...prev, [key]: val }))

  const result = useMemo(() => calculateNetWorth({ assets, liabilities }), [assets, liabilities])

  const healthColorMap = { positive: colors.positive, warning: colors.warning, negative: colors.negative }

  const handleExportPDF = async () => {
    trackExportReport()
    const { default: jsPDF } = await import('jspdf')
    const { default: autoTable } = await import('jspdf-autotable')

    const doc = new jsPDF()
    const pageWidth = doc.internal.pageSize.getWidth()
    const pageHeight = doc.internal.pageSize.getHeight()
    const margin = 14

    const ensureSpace = (needed) => {
      if ((doc.lastAutoTable?.finalY || margin) + needed > pageHeight - margin) {
        doc.addPage()
        return margin + 4
      }
      return (doc.lastAutoTable?.finalY || margin) + 10
    }

    const addSectionTitle = (title, y) => {
      doc.setFontSize(13)
      doc.setTextColor(40, 40, 40)
      doc.text(title, margin, y)
      return y + 6
    }

    // ── Header ──
    doc.setFillColor(255, 255, 255)
    doc.rect(0, 0, pageWidth, pageHeight, 'F')
    doc.setFontSize(22)
    doc.setTextColor(30, 30, 30)
    doc.text('Net Worth Report', margin, 22)
    doc.setFontSize(10)
    doc.setTextColor(128, 128, 128)
    doc.text(`Generated by Liquid Finance  |  ${new Date().toLocaleDateString()}`, margin, 30)
    doc.setDrawColor(220, 220, 220)
    doc.line(margin, 33, pageWidth - margin, 33)

    // ── Section 1: Assets ──
    let y = 40
    y = addSectionTitle('Assets', y)

    const assetRows = [
      ['Cash & Savings', formatCurrency(assets.cash)],
      ['Investments (401k, IRA, Brokerage)', formatCurrency(assets.investments)],
      ['Property (Home Value)', formatCurrency(assets.property)],
      ['Vehicles', formatCurrency(assets.vehicles)],
      ['Other Assets', formatCurrency(assets.other)],
    ]

    autoTable(doc, {
      head: [['Asset', 'Value']],
      body: [
        ...assetRows,
        [{ content: 'Total Assets', styles: { fontStyle: 'bold' } }, { content: formatCurrency(result.totalAssets), styles: { fontStyle: 'bold' } }],
      ],
      startY: y,
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 4 },
      headStyles: { fillColor: [34, 197, 94], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 250, 252] },
    })

    // ── Section 2: Liabilities ──
    y = ensureSpace(60)
    y = addSectionTitle('Liabilities', y)

    const liabilityRows = [
      ['Mortgage', formatCurrency(liabilities.mortgage)],
      ['Student Loans', formatCurrency(liabilities.studentLoans)],
      ['Auto Loans', formatCurrency(liabilities.autoLoans)],
      ['Credit Cards', formatCurrency(liabilities.creditCards)],
      ['Other Debt', formatCurrency(liabilities.other)],
    ]

    autoTable(doc, {
      head: [['Liability', 'Value']],
      body: [
        ...liabilityRows,
        [{ content: 'Total Liabilities', styles: { fontStyle: 'bold' } }, { content: formatCurrency(result.totalLiabilities), styles: { fontStyle: 'bold' } }],
      ],
      startY: y,
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 4 },
      headStyles: { fillColor: [239, 68, 68], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [254, 242, 242] },
    })

    // ── Section 3: Key Results (stat cards) ──
    y = ensureSpace(60)
    y = addSectionTitle('Key Results', y)

    const netWorthDisplay = `${result.isPositive ? '' : '-'}${formatCurrency(Math.abs(result.netWorth))}`

    autoTable(doc, {
      head: [['Metric', 'Value']],
      body: [
        ['Net Worth', netWorthDisplay],
        ['Total Assets', formatCurrency(result.totalAssets)],
        ['Total Debt', formatCurrency(result.totalLiabilities)],
        ['Debt-to-Asset Ratio', `${result.debtToAssetRatio}%`],
      ],
      startY: y,
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 4 },
      headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 250, 252] },
    })

    // ── Section 4: Financial Health Badge ──
    y = ensureSpace(30)
    y = addSectionTitle('Financial Health', y)

    const healthDescriptions = {
      'Debt-Free': 'No liabilities -- outstanding!',
      'Healthy': 'Debt-to-asset ratio under 30% -- strong position',
      'Moderate': 'Debt-to-asset ratio 30-50% -- room for improvement',
      'High Leverage': 'Debt-to-asset ratio 50-80% -- consider a debt payoff plan',
      'Over-Leveraged': 'Debt-to-asset ratio over 80% -- prioritize debt reduction',
    }

    autoTable(doc, {
      head: [['Status', 'Description']],
      body: [
        [result.healthStatus, healthDescriptions[result.healthStatus] || ''],
      ],
      startY: y,
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 4 },
      headStyles: { fillColor: [99, 102, 241], textColor: 255, fontStyle: 'bold' },
    })

    // ── Section 5: Asset Allocation Breakdown ──
    if (result.assetAllocation.length > 0) {
      y = ensureSpace(60)
      y = addSectionTitle('Asset Allocation', y)

      const allocationRows = result.assetAllocation.map(a => [
        a.name,
        formatCurrency(a.value),
        `${(a.value / result.totalAssets * 100).toFixed(1)}%`,
      ])

      autoTable(doc, {
        head: [['Asset Category', 'Value', '% of Total']],
        body: allocationRows,
        startY: y,
        theme: 'striped',
        styles: { fontSize: 9, cellPadding: 4 },
        headStyles: { fillColor: [34, 197, 94], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [240, 253, 244] },
      })
    }

    // ── Section 6: Liability Breakdown ──
    if (result.liabilityBreakdown.length > 0) {
      y = ensureSpace(60)
      y = addSectionTitle('Liability Breakdown', y)

      const breakdownRows = result.liabilityBreakdown.map(l => [
        l.name,
        formatCurrency(l.value),
        `${(l.value / result.totalLiabilities * 100).toFixed(1)}%`,
      ])

      autoTable(doc, {
        head: [['Liability Category', 'Value', '% of Total']],
        body: breakdownRows,
        startY: y,
        theme: 'striped',
        styles: { fontSize: 9, cellPadding: 4 },
        headStyles: { fillColor: [239, 68, 68], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [254, 242, 242] },
      })
    }

    // ── Section 7: Chart Captures ──
    if (assetChartRef.current) {
      await addChartToPDF(doc, assetChartRef, { title: 'Asset Allocation Chart' })
    }
    if (liabilityChartRef.current) {
      await addChartToPDF(doc, liabilityChartRef, { title: 'Liability Breakdown Chart' })
    }

    // ── Footer on every page ──
    const totalPages = doc.getNumberOfPages()
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i)
      doc.setFillColor(255, 255, 255)
      doc.setFontSize(8)
      doc.setTextColor(180, 180, 180)
      doc.text(`Liquid Finance  |  Net Worth Report  |  Page ${i} of ${totalPages}`, margin, pageHeight - 8)
    }

    doc.save(`net-worth-report-${Date.now()}.pdf`)
  }

  return (
    <div className="space-y-6">
      {/* Header with Export */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-heading">Assets & Liabilities</h3>
        <Button variant="secondary" size="sm" onClick={handleExportPDF} disabled={!isPro} title={!isPro ? 'Pro feature' : ''}>
          Export PDF{!isPro ? ' (Pro)' : ''}
        </Button>
      </div>

      {/* Assets & Liabilities Side by Side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <GlassCard>
          <h3 className="text-lg font-semibold text-heading mb-4">Assets</h3>
          <div className="space-y-3">
            <InputField label="Cash & Savings" value={assets.cash} onChange={v => updateAsset('cash', v)} prefix="$" />
            <InputField label="Investments (401k, IRA, Brokerage)" value={assets.investments} onChange={v => updateAsset('investments', v)} prefix="$" />
            <InputField label="Property (Home Value)" value={assets.property} onChange={v => updateAsset('property', v)} prefix="$" />
            <InputField label="Vehicles" value={assets.vehicles} onChange={v => updateAsset('vehicles', v)} prefix="$" />
            <InputField label="Other Assets" value={assets.other} onChange={v => updateAsset('other', v)} prefix="$" />
          </div>
          <div className="mt-4 pt-3 border-t border-card flex justify-between text-sm font-semibold">
            <span className="text-heading">Total Assets</span>
            <span style={{ color: colors.positive }}>{formatCurrency(result.totalAssets)}</span>
          </div>
        </GlassCard>

        <GlassCard>
          <h3 className="text-lg font-semibold text-heading mb-4">Liabilities</h3>
          <div className="space-y-3">
            <InputField label="Mortgage" value={liabilities.mortgage} onChange={v => updateLiability('mortgage', v)} prefix="$" />
            <InputField label="Student Loans" value={liabilities.studentLoans} onChange={v => updateLiability('studentLoans', v)} prefix="$" />
            <InputField label="Auto Loans" value={liabilities.autoLoans} onChange={v => updateLiability('autoLoans', v)} prefix="$" />
            <InputField label="Credit Cards" value={liabilities.creditCards} onChange={v => updateLiability('creditCards', v)} prefix="$" />
            <InputField label="Other Debt" value={liabilities.other} onChange={v => updateLiability('other', v)} prefix="$" />
          </div>
          <div className="mt-4 pt-3 border-t border-card flex justify-between text-sm font-semibold">
            <span className="text-heading">Total Liabilities</span>
            <span style={{ color: colors.negative }}>{formatCurrency(result.totalLiabilities)}</span>
          </div>
        </GlassCard>
      </div>

      {/* Key Results */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <ResultCard
          label="Net Worth"
          value={`${result.isPositive ? '' : '-'}${formatCurrency(Math.abs(result.netWorth))}`}
          color={result.isPositive ? colors.positive : colors.negative}
          highlight
        />
        <ResultCard label="Total Assets" value={formatCurrency(result.totalAssets)} color={colors.positive} />
        <ResultCard label="Total Debt" value={formatCurrency(result.totalLiabilities)} color={colors.negative} />
        <ResultCard
          label="Debt-to-Asset Ratio"
          value={`${result.debtToAssetRatio}%`}
          color={healthColorMap[result.healthColor] || colors.primary}
        />
      </div>

      {/* Health Badge */}
      <GlassCard className="!p-4">
        <div className="flex items-center gap-3">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: healthColorMap[result.healthColor] }} />
          <span className="text-sm font-semibold text-heading">{result.healthStatus}</span>
          <span className="text-xs text-muted">
            {result.healthStatus === 'Debt-Free' && 'No liabilities — outstanding!'}
            {result.healthStatus === 'Healthy' && 'Debt-to-asset ratio under 30% — strong position'}
            {result.healthStatus === 'Moderate' && 'Debt-to-asset ratio 30-50% — room for improvement'}
            {result.healthStatus === 'High Leverage' && 'Debt-to-asset ratio 50-80% — consider a debt payoff plan'}
            {result.healthStatus === 'Over-Leveraged' && 'Debt-to-asset ratio over 80% — prioritize debt reduction'}
          </span>
        </div>
      </GlassCard>

      {/* Pie Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {result.assetAllocation.length > 0 && (
          <GlassCard>
            <h3 className="text-sm font-semibold text-heading mb-4">Asset Allocation</h3>
            <div className="h-48" ref={assetChartRef}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={result.assetAllocation} dataKey="value" cx="50%" cy="50%" outerRadius={70} paddingAngle={2}>
                    {result.assetAllocation.map((entry, idx) => (
                      <Cell key={idx} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={v => formatCurrency(v)} contentStyle={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 12 }} />
                  <Legend formatter={(value) => <span className="text-xs text-muted">{value}</span>} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </GlassCard>
        )}

        {result.liabilityBreakdown.length > 0 && (
          <GlassCard>
            <h3 className="text-sm font-semibold text-heading mb-4">Liability Breakdown</h3>
            <div className="h-48" ref={liabilityChartRef}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={result.liabilityBreakdown} dataKey="value" cx="50%" cy="50%" outerRadius={70} paddingAngle={2}>
                    {result.liabilityBreakdown.map((entry, idx) => (
                      <Cell key={idx} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={v => formatCurrency(v)} contentStyle={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 12 }} />
                  <Legend formatter={(value) => <span className="text-xs text-muted">{value}</span>} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </GlassCard>
        )}
      </div>
    </div>
  )
}
